import { EVENT_CODE } from '@sgui-plus/utils/aria'
import { buildProps, definePropType } from '@sgui-plus/utils/props'
import { createCollectionWithScope } from '@sgui-plus/components/collection'
import {
  useTooltipContentProps,
} from '@sgui-plus/components/tooltip'
import type { ExtractPropTypes } from 'vue'

import type { Placement } from '@sgui-plus/components/popper'
import type { ComponentInternalInstance, ComputedRef, InjectionKey } from 'vue'
import type { Nullable } from '@sgui-plus/utils/types'

export interface ISgDropdownInstance {
  instance?: ComponentInternalInstance
  dropdownSize?: ComputedRef<string>
  handleClick?: () => void
  commandHandler?: (...arg) => void
  show?: () => void
  hide?: () => void
  trigger?: ComputedRef<string>
  hideOnClick?: ComputedRef<boolean>
  triggerElm?: ComputedRef<Nullable<HTMLButtonElement>>
}


export const dropdownProps = {
  trigger: {
    type: String,
    values: ['hover', 'custom', 'click', 'contextMenu'],
    default: 'hover'
  },
  visible: useTooltipContentProps.visible,
  transfer: useTooltipContentProps.transfer,
  ...buildProps({
    placement: {
      type: definePropType<Placement>(String),
      default: 'bottom-start',
    },
    hideAfterClick: {
      type: Boolean,
      default: true,
    },
    maxHeight: {
      type: definePropType<number | string>([Number, String]),
      default: '',
    },
    dropClassName: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: ''
    },
    dropdownStyle: {
      type: String,
      values: ['normal', 'simple'],
      default: 'normal'
    },
    calculateDropdownMinWidth: {
      type: Boolean,
      default: false
    }
  } as const),
} as const

export const dropdownItemProps = buildProps({
  name: {
    type: [Object, String, Number],
    default: () => ({}),
  },
  disabled: Boolean,
  divided: Boolean,
  textValue: String,
  selected: {
    type: Boolean,
    default: false,
  },
} as const)

export const dropdownMenuProps = buildProps({
  onKeydown: { type: definePropType<(e: KeyboardEvent) => void>(Function) },
})

export const FIRST_KEYS = [
  EVENT_CODE.down,
  EVENT_CODE.pageDown,
  EVENT_CODE.home,
]

export const LAST_KEYS = [EVENT_CODE.up, EVENT_CODE.pageUp, EVENT_CODE.end]

export const FIRST_LAST_KEYS = [...FIRST_KEYS, ...LAST_KEYS]

const {
  SgCollection,
  SgCollectionItem,
  COLLECTION_INJECTION_KEY,
  COLLECTION_ITEM_INJECTION_KEY,
} = createCollectionWithScope('Dropdown')

export {
  SgCollection,
  SgCollectionItem,
  COLLECTION_INJECTION_KEY as DROPDOWN_COLLECTION_INJECTION_KEY,
  COLLECTION_ITEM_INJECTION_KEY as DROPDOWN_COLLECTION_ITEM_INJECTION_KEY,
}

export type DropdownProps = ExtractPropTypes<typeof dropdownProps>
export type DropdownItemProps = ExtractPropTypes<typeof dropdownItemProps>

export interface DropdownContext {
  onClickItem: Function
}

export const DropdownContextKey: InjectionKey<DropdownContext> = Symbol('DropdownContextKey')
